import type { ResultRow } from './resultRows.js';
import { asRecord, num, strings, text } from './resultValues.js';

const RECOMMENDATIONS = 'recommendations';
const RESULT_TYPE = 'result_type';
const STATUS = 'status';
const ERROR = 'error';
const COMPLETED = 'Completed';
const ITERATIONS_TYPES = new Set(['iterations', 'preflight_iterations']);
const SCORE_PRECISION = 2;

export interface TagChange {
  action: string | null;
  feature: string;
  attribute: string | null;
  from: string | null;
  to: string;
}

export interface Recommendation {
  key: string;
  // The service's own 1-based id within the creative — what a pick is keyed by.
  id: number | null;
  title: string;
  action: string | null;
  attribute: string | null;
  rationale: string | null;
  evidence: string[];
  impact: string | null;
  // Which way the impact points, for the chip's arrow. A stated `kpi_increase`
  // is an increase by name, so only a computed movement can point down.
  impactUp: boolean;
  changes: TagChange[];
}

export interface RowOutcome {
  failed: boolean;
  error: string | null;
  score: number | null;
  bestPracticeScore: number | null;
  performanceScore: number | null;
  brandGuidelinesScore: number | null;
  recommendations: Recommendation[];
}

function changesOf(value: unknown): TagChange[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const change = asRecord(entry);
    if (change === null) return [];
    const feature = text(change['feature']);
    const to = text(change['proposed_tag']);
    if (feature === null || to === null) return [];
    return [
      {
        action: text(change['action']),
        feature,
        attribute: text(change['attribute']),
        from: text(change['current_tag']),
        to,
      },
    ];
  });
}

function sharedAction(changes: TagChange[]): string | null {
  const actions = new Set(changes.flatMap((change) => change.action ?? []));
  const [only] = [...actions];
  return actions.size === 1 && only !== undefined ? only : null;
}

// The arrow carries the sign, so the text drops it. A stated lift's direction is its
// first signed number ("up to -5% CPA" points down); unsigned reads as up.
const STATED_SIGN = /[+-](?=\d)/;

function impactOf(entry: Record<string, unknown>): string | null {
  const stated = text(entry['kpi_increase']);
  if (stated !== null) return stated.replace(STATED_SIGN, '');

  const delta = roundedDelta(entry);
  const projected = num(entry['projected_final_overall_score']);
  if (delta === null || projected === null) return null;
  return `${Math.abs(delta).toFixed(SCORE_PRECISION)} → ${projected.toFixed(SCORE_PRECISION)}`;
}

// Rounded first, so a delta that shows as 0.00 never points down.
function roundedDelta(entry: Record<string, unknown>): number | null {
  const delta = num(entry['projected_final_delta']);
  return delta === null ? null : Number(delta.toFixed(SCORE_PRECISION));
}

function impactUpOf(entry: Record<string, unknown>): boolean {
  const stated = text(entry['kpi_increase']);
  if (stated !== null) return stated.match(STATED_SIGN)?.[0] !== '-';
  return (roundedDelta(entry) ?? 0) >= 0;
}

function parseRecommendations(value: unknown): Recommendation[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry, index) => {
    const record = asRecord(entry);
    if (record === null) return [];
    const changes = changesOf(record['tag_changes']);
    return [
      {
        key: String(num(record['id']) ?? index),
        id: num(record['id']),
        title: text(record['title']) ?? changes[0]?.feature ?? 'Recommendation',
        action: sharedAction(changes),
        attribute: text(record['attribute']),
        rationale: text(record['rationale']),
        evidence: strings(record['evidence']),
        impact: impactOf(record),
        impactUp: impactUpOf(record),
        changes,
      },
    ];
  });
}

export function recommendationsOf(holder: Record<string, unknown>): Recommendation[] {
  return parseRecommendations(holder[RECOMMENDATIONS]);
}

export function isIterationsResult(result: unknown): boolean {
  const holder = asRecord(result);
  const type = holder === null ? null : text(holder[RESULT_TYPE]);
  return type !== null && ITERATIONS_TYPES.has(type);
}

export function outcomeOfRow(row: ResultRow): RowOutcome {
  const status = text(row.values[STATUS]);
  const error = text(row.values[ERROR]);

  return {
    failed: error !== null || (status !== null && status !== COMPLETED),
    error,
    score: num(row.values['final_overall_score']),
    bestPracticeScore: num(row.values['best_practice_overall_score']),
    performanceScore: num(row.values['performance_overall_score']),
    brandGuidelinesScore: num(row.values['brand_guidelines_overall_score']),
    recommendations: recommendationsOf(row.values),
  };
}

export function formatScore(score: number | null): string {
  return score === null ? '—' : score.toFixed(SCORE_PRECISION);
}

export function pickedForRun(
  rows: ResultRow[],
  picked: Record<string, number[]>,
): Record<string, number[]> {
  return Object.fromEntries(
    rows.flatMap((row) => {
      const ids = row.creativeId === null ? undefined : picked[row.creativeId];
      return row.creativeId === null || ids === undefined || ids.length === 0
        ? []
        : [[row.creativeId, [...ids].sort((first, second) => first - second)]];
    }),
  );
}
